import { Component, OnInit, OnDestroy, HostListener, ViewEncapsulation, inject, viewChild } from '@angular/core';
import { Router, NavigationEnd, RouterLink } from '@angular/router';

import { Subject } from 'rxjs';
import { take, takeUntil, filter } from 'rxjs/operators';
import { PerfectScrollbarDirective } from '@core/directives/core-perfect-scrollbar/core-perfect-scrollbar';

import { CoreConfigService } from '@core/services/config';
import { CoreMenuService } from '@core/components/core-menu/core-menu.service';
import { CoreSidebarService } from '@core/components/core-sidebar/core-sidebar.service';
import { AuthService } from 'app/common/services/auth';
import { NgClass } from '@angular/common';
import { DefaultClassDirective } from '@ngbracket/ngx-layout/extended';
import { FeatherIconDirective } from '../../../../../@core/directives/core-feather-icons/core-feather-icons';
import { PerfectScrollbarDirective as PerfectScrollbarDirective_1 } from '../../../../../@core/directives/core-perfect-scrollbar/core-perfect-scrollbar';
import { CoreMenuComponent } from '../../../../../@core/components/core-menu/core-menu';

@Component({
    selector: 'vertical-menu',
    templateUrl: './vertical-menu.html',
    styleUrl: './vertical-menu.scss',
    encapsulation: ViewEncapsulation.None,
    imports: [RouterLink, NgClass, DefaultClassDirective, FeatherIconDirective, PerfectScrollbarDirective_1, CoreMenuComponent]
})
export class VerticalMenuComponent implements OnInit, OnDestroy {
  private _coreConfigService = inject(CoreConfigService);
  private _coreMenuService = inject(CoreMenuService);
  private _coreSidebarService = inject(CoreSidebarService);
  private _router = inject(Router);
  private _authService = inject(AuthService);

  coreConfig: any;
  menu: any;
  isCollapsed: boolean;
  isScrolled: boolean = false;
  public userValue: any = {
    username: null,
    fullname: null,
    branchCode: null,
    warehouse: null
  };

  // Private
  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   *
   * @param {CoreConfigService} _coreConfigService
   * @param {CoreMenuService} _coreMenuService
   * @param {CoreSidebarService} _coreSidebarService
   * @param {Router} _router
   */
  constructor() {
    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  readonly directiveRef = viewChild(PerfectScrollbarDirective);

  // Lifecycle Hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On Init
   */
  ngOnInit(): void {
    // Subscribe config change
    this._coreConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe(config => {
      this.coreConfig = config;
    });

    this.isCollapsed = this._coreSidebarService.getSidebarRegistry('menu').collapsed;

    // this.userValue.username = this._authService.currentUserValue.userName;
    // this.userValue.fullname = this._authService.currentUserValue.fullName;
    // this.userValue.branchCode = this._authService.currentUserValue.branch;
    // this.userValue.warehouse = this._authService.currentUserValue.warehouse;

    // Close the menu on router NavigationEnd (Required for small screen to close the menu on select)
    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(() => {
        if (this._coreSidebarService.getSidebarRegistry('menu')) {
          this._coreSidebarService.getSidebarRegistry('menu').close();
        }
      });

    // scroll to active on navigation end
    this._router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        take(1)
      )
      .subscribe(() => {
        setTimeout(() => {
          this.directiveRef().scrollToElement('.navigation .active', -180, 500);
        });
      });

    // Get current menu
    this._coreMenuService.onMenuChanged
      .pipe(
        filter(value => value !== null),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe(() => {
        this.menu = this._coreMenuService.getCurrentMenu();
      });
  }

  /**
   * On Destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(true);
    this._unsubscribeAll.complete();
  }

  // Public Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * On Sidebar scroll set isScrolled as true
   */
  onSidebarScroll(): void {
    if (this.directiveRef().position(true).y > 3) {
      this.isScrolled = true;
    } else {
      this.isScrolled = false;
    }
  }

  /**
   * Toggle sidebar expanded status
   */
  toggleSidebar(): void {
    this._coreSidebarService.getSidebarRegistry('menu').toggleOpen();
  }

  /**
   * Toggle sidebar collapsed status
   */
  toggleSidebarCollapsible(): void {
    // Get the current menu state
    this._coreConfigService
      .getConfig()
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(config => {
        this.isCollapsed = config.layout.menu.collapsed;
      });

    if (this.isCollapsed) {
      this._coreConfigService.setConfig({ layout: { menu: { collapsed: false } } }, { emitEvent: true });
    } else {
      this._coreConfigService.setConfig({ layout: { menu: { collapsed: true } } }, { emitEvent: true });
    }
  }
}
